import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import LatexRenderer from "../components/atoms/LatexRenderer";
import DiffPip from "../components/atoms/DiffPip";
import { getPotd, submitPotd } from "../services/api";

export default function PotdPage() {
  const { t } = useTheme();
  const { user } = useAuth();
  const [potd, setPotd] = useState(null);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setPotd(await getPotd());
    } catch (err) {
      setError(err.message || "Unable to load today's POTD.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!answer.trim() || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const submission = await submitPotd(answer);
      setResult(submission);
      setAnswer("");
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", padding: "100px 32px 80px" }}>
      <div style={{ marginBottom: 32 }}>
        <span style={{ fontSize: 11, color: t.text3, letterSpacing: "0.12em", fontWeight: 700 }}>DAILY CHALLENGE</span>
        <h1 style={{ fontSize: 38, color: t.text, margin: "8px 0 10px", fontFamily: "'Playfair Display',Georgia,serif" }}>Problem of the Day</h1>
        <p style={{ color: t.text2, fontSize: 14, margin: 0 }}>One problem. The same challenge for everyone.</p>
      </div>

      {loading ? <p style={{ color: t.text3 }}>Loading today&apos;s problem...</p> : error ? (
        <div style={{ color: t.text2 }}><p>{error}</p><button onClick={load} style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 8, padding: "9px 16px", cursor: "pointer" }}>Try again</button></div>
      ) : (
        <div style={{ width: "100%" }}>
          <section style={{ background: t.bg2, border: `0.5px solid ${t.border}`, borderRadius: 14, padding: "30px clamp(24px, 5vw, 56px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 18 }}>
              <span style={{ fontSize: 11, color: t.text3 }}>{potd.date}</span>
              <DiffPip level={potd.problem.difficulty} />
            </div>
            <h2 style={{ fontSize: 25, color: t.text, margin: "0 0 18px", fontFamily: "'Playfair Display',Georgia,serif" }}>{potd.problem.title}</h2>
            <div style={{ maxWidth: 920, color: t.text, fontSize: 16, lineHeight: 1.95, fontFamily: "Georgia,serif", marginBottom: 28 }}>
              <LatexRenderer text={potd.problem.fullStatement || potd.problem.preview} />
            </div>
            {!user ? <p style={{ color: t.text3, fontSize: 13 }}>Sign in to submit an answer and earn points.</p> : (
              <form onSubmit={handleSubmit} style={{ maxWidth: 720 }}>
                <label style={{ display: "block", color: t.text2, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 7 }}>YOUR ANSWER</label>
                <input value={answer} onChange={(event) => setAnswer(event.target.value)} disabled={Boolean(result?.submitted) || busy} placeholder="Enter the canonical answer" style={{ width: "100%", boxSizing: "border-box", height: 42, padding: "0 12px", border: `0.5px solid ${t.border2}`, borderRadius: 8, background: t.inputBg, color: t.text, fontFamily: "inherit", outline: "none" }} />
                <button type="submit" disabled={busy || Boolean(result?.submitted)} style={{ marginTop: 10, background: t.primary, color: t.primaryFg, border: "none", borderRadius: 8, padding: "10px 17px", fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1 }}>{busy ? "Checking..." : "Submit answer"}</button>
                {result && <p style={{ color: result.error || !result.correct ? "#c0392b" : "#2d7a4f", fontSize: 13, margin: "14px 0 0" }}>{result.error || (result.correct ? `Correct. +${result.pointsAwarded} points.` : "Incorrect. Your attempt has been used.")}{result.correct && !result.alreadySubmitted ? " Submission recorded for today." : result.alreadySubmitted ? " You have already submitted today." : ""}</p>}
              </form>
            )}
          </section>
        </div>
      )}
    </div>
  );
}